// Bespoke per-faction worker rigs. The Iron Covenant worker keeps the shared instanced
// straw-hat laborer; the other three factions get a distinct silhouette so each side's
// economy reads at a glance (owner request). Fully procedural (plan §29.4).
//   Ashen Host   — hunched skeletal drudge (skull, ribs, ghostfire eyes)
//   Verdant Wilds— squat mossy root-sprite (bark limbs, leaf tuft)
//   Astral Dominion — hovering crystalline construct (shard core, halo, no legs)
// Animatable named parts: `legL` / `legR` (walkers), `carry` (hauled crate, toggled), `halo`.

import * as THREE from 'three';
import { OWNER_COLORS } from './unitStyles';

export const BESPOKE_WORKER_DEFS = new Set(['u-ah-worker', 'u-vw-worker', 'u-ad-worker', 'u-vw-barkwright']);

export function isHoverWorker(defId: string): boolean {
  return defId === 'u-ad-worker';
}

function mix(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}
function mat(color: THREE.Color | number, rough = 0.9, metal = 0.05): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({
    color: color instanceof THREE.Color ? color : new THREE.Color(color),
    roughness: rough,
    metalness: metal,
    flatShading: true,
  });
}
function glow(color: number, i = 1.1): THREE.MeshStandardMaterial {
  return new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: i, roughness: 0.3 });
}

function legPair(m: THREE.Material, y: number, spread: number, len: number): [THREE.Group, THREE.Group] {
  const mk = (sx: number, name: string): THREE.Group => {
    const hip = new THREE.Group();
    hip.name = name;
    hip.position.set(sx * spread, y, 0);
    const shin = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.05, len, 5), m);
    shin.position.y = -len / 2;
    hip.add(shin);
    return hip;
  };
  return [mk(-1, 'legL'), mk(1, 'legR')];
}

/** A hauled-resource crate, hidden until the worker is carrying. */
function addCarry(g: THREE.Group, y: number): void {
  const crate = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.34, 0.34), mat(0x8a6a3a, 0.8));
  crate.name = 'carry';
  crate.position.set(0, y, 0.34);
  crate.visible = false;
  g.add(crate);
}

function makeBarkwrightCircle(owner: 0 | 1): THREE.Group {
  const group = new THREE.Group();
  group.name = 'barkwrightCircle';
  const bark = mat(mix(0x765332, OWNER_COLORS[owner], 0.1), 0.96);
  const moss = mat(mix(0x647f43, OWNER_COLORS[owner], 0.12), 0.98);
  const leather = mat(0x493323, 0.92);
  const root = mat(0x9a7245, 0.98);
  const metal = mat(0xa78f62, 0.55, 0.35);
  const offsets = [
    [-0.48, 0.35],
    [0.48, 0.35],
    [-0.48, -0.4],
    [0.48, -0.4],
  ] as const;

  offsets.forEach(([x, z], i) => {
    const druid = new THREE.Group();
    druid.name = `barkDruid${i}`;
    druid.position.set(x, 0, z);
    druid.userData.baseY = 0;
    const robe = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.22, 0.62, 7), moss);
    robe.position.y = 0.54;
    druid.add(robe);
    const chest = new THREE.Mesh(new THREE.CapsuleGeometry(0.14, 0.32, 3, 7), bark);
    chest.position.y = 0.9;
    druid.add(chest);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.14, 8, 6), bark);
    head.position.set(0, 1.19, 0.02);
    druid.add(head);

    // Heavy wooden shoulder frame: crossbeam, upright braces, and owner-colored knot.
    const frame = new THREE.Group();
    frame.position.y = 1.0;
    const cross = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.07, 0.09), root);
    frame.add(cross);
    for (const sx of [-1, 1]) {
      const brace = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.42, 0.07), root);
      brace.position.set(sx * 0.21, 0.08, -0.02);
      brace.rotation.z = sx * -0.16;
      frame.add(brace);
    }
    const knot = new THREE.Mesh(new THREE.OctahedronGeometry(0.07, 0), mat(OWNER_COLORS[owner], 0.65, 0.1));
    knot.position.z = 0.08;
    frame.add(knot);
    druid.add(frame);

    // Tool belt and practical pouches.
    const belt = new THREE.Mesh(new THREE.TorusGeometry(0.18, 0.035, 5, 12), leather);
    belt.rotation.x = Math.PI / 2;
    belt.position.y = 0.72;
    druid.add(belt);
    for (const sx of [-1, 1]) {
      const pouch = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.14, 0.08), leather);
      pouch.position.set(sx * 0.16, 0.64, 0.13);
      druid.add(pouch);
      const leg = new THREE.Group();
      leg.name = `barkLeg${sx < 0 ? 'L' : 'R'}${i}`;
      leg.position.set(sx * 0.09, 0.34, 0);
      const limb = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.06, 0.38, 5), bark);
      limb.position.y = -0.19;
      leg.add(limb);
      druid.add(leg);

      const arm = new THREE.Group();
      arm.name = `barkArm${sx < 0 ? 'L' : 'R'}${i}`;
      arm.position.set(sx * 0.22, 0.94, 0.04);
      const upper = new THREE.Mesh(new THREE.CylinderGeometry(0.045, 0.055, 0.4, 5), bark);
      upper.position.y = -0.2;
      arm.add(upper);
      const gauntlet = new THREE.Mesh(new THREE.IcosahedronGeometry(0.09, 0), metal);
      gauntlet.position.y = -0.43;
      arm.add(gauntlet);
      // Manipulating root fingers curl out from each practical gauntlet.
      for (let f = -1; f <= 1; f++) {
        const finger = new THREE.Mesh(new THREE.CylinderGeometry(0.012, 0.018, 0.18, 4), root);
        finger.position.set(f * 0.035, -0.53, 0.04 + Math.abs(f) * 0.015);
        finger.rotation.x = 0.28;
        arm.add(finger);
      }
      druid.add(arm);
    }

    // A crooked living staff and a cable that reaches from its crook into the soil.
    const staff = new THREE.Group();
    staff.name = `barkStaff${i}`;
    staff.position.set(0.28, 0.78, 0.08);
    const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.025, 0.04, 1.18, 5), root);
    shaft.position.y = -0.12;
    shaft.rotation.z = -0.1;
    staff.add(shaft);
    const hook = new THREE.Mesh(new THREE.TorusGeometry(0.13, 0.028, 5, 10, Math.PI * 1.25), root);
    hook.position.set(-0.07, 0.48, 0);
    hook.rotation.z = 0.22;
    staff.add(hook);
    const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.014, 0.025, 0.82, 5), root);
    cable.name = `barkCable${i}`;
    cable.position.set(-0.07, -0.36, 0.02);
    cable.rotation.x = 0.16;
    staff.add(cable);
    druid.add(staff);
    group.add(druid);
  });

  // Interlocking root courses appear one layer at a time while constructing or repairing.
  const weave = new THREE.Group();
  weave.name = 'rootWeave';
  weave.visible = false;
  for (let layer = 0; layer < 4; layer++) {
    const course = new THREE.Mesh(new THREE.TorusGeometry(0.64 - layer * 0.06, 0.035, 5, 18, Math.PI * 1.65), root);
    course.name = `rootCourse${layer}`;
    course.rotation.x = Math.PI / 2;
    course.rotation.z = layer % 2 === 0 ? 0.25 : Math.PI + 0.25;
    course.position.y = 0.08 + layer * 0.11;
    weave.add(course);
  }
  group.add(weave);
  addCarry(group, 0.72);
  group.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return group;
}

export function poseBarkwrightCircle(group: THREE.Group, time: number, phase: number, moveAmt: number, working: boolean): void {
  for (let i = 0; i < 4; i++) {
    const offsetPhase = phase + i * 1.35;
    const stride = Math.sin(offsetPhase) * 0.55 * moveAmt;
    const legL = group.getObjectByName(`barkLegL${i}`);
    const legR = group.getObjectByName(`barkLegR${i}`);
    if (legL) legL.rotation.x = stride;
    if (legR) legR.rotation.x = -stride;
    const weaveMotion = working ? Math.sin(time * 3.2 + i) * 0.45 : Math.sin(time * 1.1 + i) * 0.045;
    const armL = group.getObjectByName(`barkArmL${i}`);
    const armR = group.getObjectByName(`barkArmR${i}`);
    if (armL) armL.rotation.x = -weaveMotion;
    if (armR) armR.rotation.x = weaveMotion - (working ? 0.45 : 0);
    const staff = group.getObjectByName(`barkStaff${i}`);
    if (staff) staff.rotation.x = working ? -0.34 + Math.sin(time * 2.4 + i) * 0.12 : Math.sin(time * 0.8 + i) * 0.025;
    const cable = group.getObjectByName(`barkCable${i}`);
    if (cable) cable.scale.y = working ? 0.75 + Math.sin(time * 2.8 + i) * 0.25 : 0.35;
    const druid = group.getObjectByName(`barkDruid${i}`);
    if (druid) druid.position.y = Math.abs(Math.sin(offsetPhase)) * 0.035 * moveAmt + Math.sin(time * 1.4 + i) * 0.012 * (1 - moveAmt);
  }
  const weave = group.getObjectByName('rootWeave');
  if (weave) weave.visible = working;
  for (let layer = 0; layer < 4; layer++) {
    const course = group.getObjectByName(`rootCourse${layer}`);
    if (!course) continue;
    const cycle = (time * 0.75) % 6;
    const growth = working
      ? cycle < 4
        ? Math.max(0.08, Math.min(1, cycle - layer))
        : cycle < 5
          ? 1
          : Math.max(0.08, 1 - (cycle - 5))
      : 0.08;
    course.scale.set(growth, growth, growth);
  }
}

export function makeWorkerRig(defId: string, owner: 0 | 1): THREE.Group {
  if (defId === 'u-vw-barkwright') return makeBarkwrightCircle(owner);
  const g = new THREE.Group();
  const team = OWNER_COLORS[owner];

  if (defId === 'u-ah-worker') {
    // hunched skeletal drudge
    const bone = mat(0xd6cdb6, 0.9);
    const dark = mat(mix(0x3a2f38, team, 0.15), 0.95);
    const spine = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.2, 0.6, 7), bone);
    spine.name = 'workerBody';
    spine.position.y = 0.66;
    spine.rotation.x = 0.35; // hunched
    g.add(spine);
    for (let i = 0; i < 3; i++) {
      const rib = new THREE.Mesh(new THREE.TorusGeometry(0.16 - i * 0.02, 0.02, 5, 8, Math.PI), bone);
      rib.rotation.x = Math.PI / 2 + 0.35;
      rib.position.set(0, 0.74 - i * 0.14, 0.06 + i * 0.04);
      g.add(rib);
    }
    const tatter = new THREE.Mesh(new THREE.ConeGeometry(0.24, 0.5, 7), dark);
    tatter.position.set(0, 0.5, -0.02);
    g.add(tatter);
    const skull = new THREE.Mesh(new THREE.SphereGeometry(0.16, 9, 7), bone);
    skull.name = 'workerHead';
    skull.position.set(0, 1.02, 0.14);
    g.add(skull);
    const jaw = new THREE.Mesh(new THREE.BoxGeometry(0.18, 0.08, 0.16), bone);
    jaw.position.set(0, 0.94, 0.2);
    g.add(jaw);
    for (const sx of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.035, 6, 5), glow(0x8affc0, 1.4));
      eye.position.set(sx * 0.06, 1.04, 0.26);
      g.add(eye);
      const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.04, 0.5, 5), bone);
      arm.name = sx < 0 ? 'workerArmL' : 'workerArmR';
      arm.position.set(sx * 0.22, 0.66, 0.16);
      arm.rotation.x = 0.9;
      g.add(arm);
    }
    const [l, r] = legPair(bone, 0.42, 0.12, 0.5);
    g.add(l, r);
    addCarry(g, 0.66);
  } else if (defId === 'u-vw-worker') {
    // squat mossy root-sprite
    const bark = mat(mix(0x6a4a2c, team, 0.1), 0.95);
    const moss = mat(mix(0x5f8a3c, team, 0.15), 1);
    const body = new THREE.Mesh(new THREE.CylinderGeometry(0.24, 0.3, 0.6, 7), bark);
    body.name = 'workerBody';
    body.position.y = 0.55;
    g.add(body);
    // mossy shoulders
    const shoulder = new THREE.Mesh(new THREE.IcosahedronGeometry(0.26, 0), moss);
    shoulder.scale.y = 0.7;
    shoulder.position.y = 0.86;
    g.add(shoulder);
    const head = new THREE.Mesh(new THREE.SphereGeometry(0.18, 9, 7), bark);
    head.name = 'workerHead';
    head.position.y = 1.06;
    g.add(head);
    // leaf tuft
    for (let i = 0; i < 3; i++) {
      const leaf = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.28, 5), moss);
      const a = (i / 3) * Math.PI * 2;
      leaf.position.set(Math.cos(a) * 0.06, 1.24, Math.sin(a) * 0.06);
      leaf.rotation.z = Math.cos(a) * 0.5;
      leaf.rotation.x = Math.sin(a) * 0.5;
      g.add(leaf);
    }
    for (const sx of [-1, 1]) {
      const eye = new THREE.Mesh(new THREE.SphereGeometry(0.03, 6, 5), glow(0xd8f08a, 0.9));
      eye.position.set(sx * 0.07, 1.08, 0.16);
      g.add(eye);
      const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.04, 0.44, 5), bark);
      arm.name = sx < 0 ? 'workerArmL' : 'workerArmR';
      arm.position.set(sx * 0.26, 0.6, 0.08);
      arm.rotation.x = 0.8;
      g.add(arm);
    }
    const [l, r] = legPair(bark, 0.34, 0.13, 0.4);
    g.add(l, r);
    addCarry(g, 0.6);
  } else {
    // Astral construct — hovers, no legs
    const shell = mat(mix(0x9aa8e0, team, 0.25), 0.4, 0.35);
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.26, 0), glow(mix(0xbcd0ff, team, 0.2).getHex(), 0.8));
    core.name = 'core';
    core.position.y = 0.8;
    g.add(core);
    const shard1 = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.4, 6), shell);
    shard1.position.y = 1.16;
    g.add(shard1);
    const shard2 = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.34, 6), shell);
    shard2.rotation.x = Math.PI;
    shard2.position.y = 0.46;
    g.add(shard2);
    // hovering hands
    for (const sx of [-1, 1]) {
      const hand = new THREE.Mesh(new THREE.IcosahedronGeometry(0.08, 0), shell);
      hand.position.set(sx * 0.32, 0.7, 0.1);
      g.add(hand);
    }
    // halo ring (spins)
    const halo = new THREE.Mesh(new THREE.TorusGeometry(0.24, 0.03, 6, 16), glow(0xcfe0ff, 0.7));
    halo.name = 'halo';
    halo.rotation.x = Math.PI / 2;
    halo.position.y = 1.06;
    g.add(halo);
    addCarry(g, 0.8);
  }

  g.traverse((o) => {
    if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return g;
}
